import { IComponentOptions, IController, IRootScopeService } from "angular";
import { IModalScope, IModalService, IModalServiceInstance } from "angular-ui-bootstrap";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { featureLocalizationMap, getPlanLabel, IUpgradeEditionPlanResolveParams, EmailVerificationComponent, IPlan, IPlanLabel } from "@gtmhub/edition-plan-change";
import { CurrentUserService } from "@gtmhub/users";
import { AccountService } from "@gtmhub/accounts/accountsService";
import { ChargebeeService } from "@gtmhub/configuration/services/chargebeeService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { FeatureUsageService } from "@gtmhub/configuration/services/featureUsageService";
import { localize } from "@gtmhub/localization";

export interface IPlanDetailsBindings {
  plan: IPlan;
  selectedPlan: IPlan;
  accountId: string;
  isAccountActive: boolean;
  isAccountTrial: boolean;
  modalClose(args: { reason: unknown }): void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface PlanDetailsCtrl extends IPlanDetailsBindings {}
export class PlanDetailsCtrl implements IController {
  planLabel: IPlanLabel;
  canUpdate: boolean;
  canUpgradeToAnnual: boolean;

  static $inject = ["$uibModal", "AccountService", "AnalyticsService", "ChargebeeService", "CurrentUserService", "FeatureUsageService", "UIErrorHandlingService"];

  constructor(
    private $uibModal: IModalService,
    private accountService: AccountService,
    private analyticsService: AnalyticsService,
    private chargebeeService: ChargebeeService,
    private currentUserService: CurrentUserService,
    private featureUsageService: FeatureUsageService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {}

  $onInit(): void {
    this.planLabel = getPlanLabel(this.plan.id);
    this.planLabel.desc = localize(this.planLabel.desc);
    this.planLabel.planFeatures = this.planLabel.planFeatures.map((featureKey) => localize(featureKey));

    this.canUpdate = this.plan.weight > this.selectedPlan.weight;
    this.canUpgradeToAnnual = this.selectedPlan.billingPeriod === "monthly" && this.plan.editionName === this.selectedPlan.editionName;
  }

  openUpgradeModal(): void {
    if (this.shouldShowFeatureLossModal()) {
      this.getFeatureUsageAndDisplayModal(false);
    } else {
      this.openUpgradePlanModal();
    }
  }

  openCbCheckout(): void {
    if (this.shouldShowFeatureLossModal()) {
      this.getFeatureUsageAndDisplayModal(true);
    } else {
      this.checkValidEmailAndProceedToCheckout();
    }
  }

  contactSales(): void {
    this.analyticsService.contactSales("contactSales");
  }

  private shouldShowFeatureLossModal = (): boolean => {
    return !this.canUpdate && this.selectedPlan.editionName !== this.plan.editionName;
  };

  private openUpgradePlanModal = (): void => {
    this.$uibModal.open({
      templateUrl: require("../../views/upgrade-plan-modal.jade"),
      windowClass: "medium-small",
      resolve: {
        resolveParams: (): IUpgradeEditionPlanResolveParams => ({
          selectedPlan: this.plan,
        }),
      },
      controller: "UpgradeEditionPlanCtrl",
    });
  };

  private checkValidEmailAndProceedToCheckout = (): void => {
    const isEmailVerified = this.currentUserService.getUserSetting<boolean>("emailVerified");

    if (!isEmailVerified) {
      interface IVeryfyEmailModalScope extends IModalScope {
        typeEmailVerificationComponent: EmailVerificationComponent;
      }

      this.$uibModal.open({
        templateUrl: require("../../views/required-email-notification.jade"),
        backdrop: false,
        windowClass: "modal-small",
        backdropClass: "dark-backdrop",
        controller: [
          "$scope",
          "$rootScope",
          "$uibModalInstance",
          ($scope: IVeryfyEmailModalScope, $rootScope: IRootScopeService, $uibModalInstance: IModalServiceInstance) => {
            $scope.typeEmailVerificationComponent = "button";

            const unregister = $rootScope.$watch("emailVerified", (emailVerified) => {
              if (emailVerified) {
                $uibModalInstance.close();
                this.getCheckoutPage();
              }
            });

            $uibModalInstance.result.finally(() => {
              unregister();
            });
          },
        ],
      });

      return;
    }

    this.getCheckoutPage();
  };

  private getFeatureUsageAndDisplayModal = (isBuyAction: boolean): void => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const $ctrl = this;
    const processToNextModal = () => {
      if (isBuyAction) {
        $ctrl.checkValidEmailAndProceedToCheckout();
      } else {
        $ctrl.openUpgradePlanModal();
      }
    };

    interface IDowngradeFeatureLossScope extends IModalScope {
      features: string[];
      plan: string;
      continue(): void;
    }

    this.featureUsageService.getFeatureUsageForUserId(this.accountId, this.plan.editionName).then(
      ({ features }) => {
        if (features?.length) {
          this.$uibModal.open({
            templateUrl: require("../../views/downgrade-feature-loss.jade"),
            windowClass: "medium-small",
            backdropClass: "dark-backdrop",
            controller: [
              "$scope",
              "$uibModalInstance",
              function ($scope: IDowngradeFeatureLossScope, $uibModalInstance: IModalServiceInstance) {
                $scope.plan = $ctrl.planLabel.name;
                $scope.features = $ctrl.localizeFeatureKeys(features);

                $scope.continue = () => {
                  $uibModalInstance.close();
                  processToNextModal();
                };
              },
            ],
          });
        } else {
          processToNextModal();
        }
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  };

  private getCheckoutPage = (): void => {
    this.accountService.getCheckoutPage({ planGroupName: this.plan.planGroupName, billingPeriod: this.plan.billingPeriod, edition: this.plan.editionName }).then(
      (checkoutPageData) => {
        this.chargebeeService.openCbCheckout(checkoutPageData.url);
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private localizeFeatureKeys = (features: string[]): string[] => {
    return features.map((feature) => (featureLocalizationMap[feature] ? localize(featureLocalizationMap[feature]) : localize(feature)));
  };
}

export const PlanDetailsComponent: IComponentOptions = {
  bindings: {
    plan: "<",
    selectedPlan: "<",
    accountId: "<",
    isAccountActive: "<",
    isAccountTrial: "<",
    modalClose: "&",
  },
  controller: PlanDetailsCtrl,
  templateUrl: require("./plan-details.jade"),
};
